// Cognito authentication: email/password (SRP) plus passwordless magic-link (custom auth).
// Uses amazon-cognito-identity-js so the SPA holds no AWS credentials — only Cognito tokens.

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import type { AppConfig } from './config';

let pool: CognitoUserPool | null = null;

function userPool(config: AppConfig): CognitoUserPool {
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.userPoolClientId,
    });
  }
  return pool;
}

export interface Session {
  idToken: string;
  accessToken: string;
  email: string | null;
  /** True when the id token carries the Cognito `admin` group (gates the admin console). */
  isAdmin: boolean;
}

function toSession(s: CognitoUserSession): Session {
  const idToken = s.getIdToken();
  const payload = idToken.decodePayload() as { email?: string; 'cognito:groups'?: string[] | string };
  // The groups claim can arrive as an array or a space/comma-joined string.
  const raw = payload['cognito:groups'];
  const groups = Array.isArray(raw)
    ? raw
    : typeof raw === 'string' && raw.length
      ? raw.replace(/^\[|\]$/g, '').split(/[,\s]+/).filter(Boolean)
      : [];
  return {
    idToken: idToken.getJwtToken(),
    accessToken: s.getAccessToken().getJwtToken(),
    email: payload.email ?? null,
    isAdmin: groups.includes('admin'),
  };
}

/** Password (SRP) sign-in. */
export function signInWithPassword(
  config: AppConfig,
  email: string,
  password: string
): Promise<Session> {
  const user = new CognitoUser({ Username: email, Pool: userPool(config) });
  const details = new AuthenticationDetails({ Username: email, Password: password });
  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (s) => resolve(toSession(s)),
      onFailure: (err) => reject(err),
      // MFA / newPasswordRequired flows are handled in a later phase; surface for now.
      mfaRequired: () => reject(new Error('MFA required — not yet implemented in this skeleton')),
      newPasswordRequired: () =>
        reject(new Error('New password required — set it in the Cognito console for now')),
    });
  });
}

/**
 * Passwordless magic-link / one-time-code sign-in (CUSTOM_AUTH). Returns a `submitCode`
 * function the UI calls once the user enters the code they received by email.
 */
export function beginPasswordless(
  config: AppConfig,
  email: string
): { submitCode: (code: string) => Promise<Session> } {
  const user = new CognitoUser({ Username: email, Pool: userPool(config) });
  user.setAuthenticationFlowType('CUSTOM_AUTH');
  const details = new AuthenticationDetails({ Username: email });

  let resolveSession!: (s: Session) => void;
  let rejectSession!: (e: unknown) => void;

  user.initiateAuth(details, {
    onSuccess: (s) => resolveSession(toSession(s)),
    onFailure: (err) => rejectSession(err),
    customChallenge: () => {
      // Challenge issued; the UI now prompts for the emailed code.
    },
  });

  return {
    submitCode: (code: string) =>
      new Promise<Session>((resolve, reject) => {
        resolveSession = resolve;
        rejectSession = reject;
        user.sendCustomChallengeAnswer(code, {
          onSuccess: (s) => resolve(toSession(s)),
          onFailure: (err) => reject(err),
          customChallenge: () => reject(new Error('Incorrect code, try again')),
        });
      }),
  };
}

/** Restore an existing session from storage, if valid. */
export function getCurrentSession(config: AppConfig): Promise<Session | null> {
  const user = userPool(config).getCurrentUser();
  if (!user) return Promise.resolve(null);
  return new Promise((resolve) => {
    user.getSession((err: Error | null, s: CognitoUserSession | null) => {
      if (err || !s || !s.isValid()) return resolve(null);
      resolve(toSession(s));
    });
  });
}

export function signOut(config: AppConfig): void {
  userPool(config).getCurrentUser()?.signOut();
}
